import { Alert, Button, Checkbox, Group, Radio, Stack, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import '@mantine/dropzone/styles.css';
import type { QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useState } from 'react';
import { getIntakeBotClient } from '../../lib/bot-client';
import { botIdentifier } from '../../lib/bot-identifier';

const CLINICAL_CONDITIONS = [
  {
    code: 'J44.9',
    system: 'http://hl7.org/fhir/sid/icd-10',
    display: 'Moderate-to-severe COPD or restrictive lung disease',
  },
  { code: 'I50.9', system: 'http://hl7.org/fhir/sid/icd-10', display: 'Congestive heart failure (NYHA III-IV)' },
  { code: 'G71.9', system: 'http://hl7.org/fhir/sid/icd-10', display: 'Neuromuscular disease affecting respiration' },
  { code: 'I69.30', system: 'http://hl7.org/fhir/sid/icd-10', display: 'History of stroke with residual deficits' },
  { code: 'F11.20', system: 'http://hl7.org/fhir/sid/icd-10', display: 'Chronic opioid use' },
  { code: 'G47.31', system: 'http://hl7.org/fhir/sid/icd-10', display: 'Suspected central sleep apnea' },
  { code: 'G47.36', system: 'http://hl7.org/fhir/sid/icd-10', display: 'Known hypoventilation syndrome' },
  { code: 'I25.110', system: 'http://hl7.org/fhir/sid/icd-10', display: 'Unstable cardiac disease' },
  {
    code: 'G47.00',
    system: 'http://hl7.org/fhir/sid/icd-10',
    display: 'Severe insomnia preventing adequate sleep time',
  },
  { code: 'G47.50', system: 'http://hl7.org/fhir/sid/icd-10', display: 'Parasomnias or nocturnal seizures' },
  { code: 'previously-inconclusive-failed-hsat', system: '', display: 'Previously inconclusive or failed HSAT' },
  { code: 'none', system: '', display: 'None of the above' },
];

const NONE_OF_THE_ABOVE_CODE = 'none';

/**
 * Enforces mutual exclusion between "None of the above" and any sibling
 * exclusion option on the Checkbox.Group's onChange. Selecting "none" clears
 * siblings; selecting any sibling clears "none". Resolution is instantly
 * visible because the component owns the state directly.
 */
export function applyNoneOfTheAboveExclusion(prev: string[], next: string[]): string[] {
  const prevHasNone = prev.includes(NONE_OF_THE_ABOVE_CODE);
  const nextHasNone = next.includes(NONE_OF_THE_ABOVE_CODE);
  const nextHasSiblings = next.some((c) => c !== NONE_OF_THE_ABOVE_CODE);

  if (!nextHasNone || !nextHasSiblings) {
    return next;
  }
  if (!prevHasNone && nextHasNone) {
    return [NONE_OF_THE_ABOVE_CODE];
  }
  return next.filter((c) => c !== NONE_OF_THE_ABOVE_CODE);
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

async function uploadFile(file: File): Promise<string> {
  const base64 = arrayBufferToBase64(await file.arrayBuffer());
  const client = await getIntakeBotClient();
  const result = (await client.executeBot(
    botIdentifier('pcp-referral-upload'),
    { data: base64, contentType: file.type, fileName: file.name },
    'application/json'
  )) as { url: string };
  return result.url;
}

export interface TriageInputsForValidation {
  previousSleepStudy: string;
  cpapBipapUse: string;
  oralApplianceUse: string;
  sleepApneaSurgery: string;
  hasCompletedStopbang: string;
  stopbangShareMethod: string;
  stopbangFile: File | null;
  hasCompletedEss: string;
  essShareMethod: string;
  essFile: File | null;
}

export function validateTriageInputs(s: TriageInputsForValidation): string | null {
  if (
    !s.previousSleepStudy ||
    !s.cpapBipapUse ||
    !s.oralApplianceUse ||
    !s.sleepApneaSurgery ||
    !s.hasCompletedStopbang ||
    !s.hasCompletedEss
  ) {
    return 'Please answer all required questions.';
  }
  if (s.hasCompletedStopbang === 'Yes' && !s.stopbangShareMethod) {
    return 'Please select how you would like to share the STOP-BANG results.';
  }
  if (s.hasCompletedEss === 'Yes' && !s.essShareMethod) {
    return 'Please select how you would like to share the ESS results.';
  }
  if (s.stopbangShareMethod === 'Upload' && !s.stopbangFile) {
    return 'Please attach the STOP-BANG file you would like to upload.';
  }
  if (s.essShareMethod === 'Upload' && !s.essFile) {
    return 'Please attach the Epworth Sleepiness Scale file you would like to upload.';
  }
  return null;
}

interface FileDropZoneProps {
  file: File | null;
  onChange: (file: File) => void;
  disabled?: boolean;
}

function FileDropZone({ file, onChange, disabled }: FileDropZoneProps): JSX.Element {
  return (
    <Dropzone
      color="#228BE6"
      onDrop={(files) => {
        if (files[0]) {
          onChange(files[0]);
        }
      }}
      accept={['application/pdf', 'text/csv']}
      maxFiles={1}
      disabled={disabled}
    >
      <Stack align="center" gap={4} style={{ pointerEvents: 'none' }}>
        <Text fw={600} c="blue" size="sm">
          Upload File
        </Text>
        <Text size="xs" c="dimmed">
          {file ? file.name : 'Accepted File Formats: PDF, CSV'}
        </Text>
      </Stack>
    </Dropzone>
  );
}

interface TriageFormProps {
  submitting: boolean;
  onSubmit: (response: QuestionnaireResponse) => void;
  onBack?: () => void;
}

export function TriageForm({ submitting, onSubmit, onBack }: TriageFormProps): JSX.Element {
  const [previousSleepStudy, setPreviousSleepStudy] = useState('');
  const [cpapBipapUse, setCpapBipapUse] = useState('');
  const [oralApplianceUse, setOralApplianceUse] = useState('');
  const [sleepApneaSurgery, setSleepApneaSurgery] = useState('');
  const [clinicalConditions, setClinicalConditions] = useState<string[]>([]);
  const [hasCompletedStopbang, setHasCompletedStopbang] = useState('');
  const [stopbangShareMethod, setStopbangShareMethod] = useState('');
  const [stopbangFile, setStopbangFile] = useState<File | null>(null);
  const [hasCompletedEss, setHasCompletedEss] = useState('');
  const [essShareMethod, setEssShareMethod] = useState('');
  const [essFile, setEssFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (): Promise<void> => {
    setError('');

    const validationError = validateTriageInputs({
      previousSleepStudy,
      cpapBipapUse,
      oralApplianceUse,
      sleepApneaSurgery,
      hasCompletedStopbang,
      stopbangShareMethod,
      stopbangFile,
      hasCompletedEss,
      essShareMethod,
      essFile,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    let stopbangUrl: string | undefined;
    let essUrl: string | undefined;

    try {
      if (stopbangShareMethod === 'Upload' && stopbangFile) {
        stopbangUrl = await uploadFile(stopbangFile);
      }
      if (essShareMethod === 'Upload' && essFile) {
        essUrl = await uploadFile(essFile);
      }
    } catch (err) {
      Sentry.captureException(err);
      setError('Failed to upload file. Please try again.');
      setUploading(false);
      return;
    }

    setUploading(false);

    const items: QuestionnaireResponseItem[] = [
      { linkId: 'previous-sleep-study', answer: [{ valueString: previousSleepStudy }] },
      { linkId: 'cpap-bipap-use', answer: [{ valueString: cpapBipapUse }] },
      { linkId: 'oral-appliance-use', answer: [{ valueString: oralApplianceUse }] },
      { linkId: 'sleep-apnea-surgery', answer: [{ valueString: sleepApneaSurgery }] },
    ];

    if (clinicalConditions.length > 0) {
      items.push({
        linkId: 'clinical-conditions',
        answer: clinicalConditions.map((code) => {
          const condition = CLINICAL_CONDITIONS.find((c) => c.code === code);
          return {
            valueCoding: condition
              ? { system: condition.system || undefined, code: condition.code, display: condition.display }
              : { code },
          };
        }),
      });
    }

    items.push({ linkId: 'has-completed-stopbang', answer: [{ valueString: hasCompletedStopbang }] });
    if (hasCompletedStopbang === 'Yes' && stopbangShareMethod) {
      items.push({ linkId: 'stopbang-share-method', answer: [{ valueString: stopbangShareMethod }] });
      if (stopbangUrl && stopbangFile) {
        items.push({
          linkId: 'stopbang-upload',
          answer: [{ valueAttachment: { url: stopbangUrl, contentType: stopbangFile.type, title: stopbangFile.name } }],
        });
      }
    }

    items.push({ linkId: 'has-completed-ess', answer: [{ valueString: hasCompletedEss }] });
    if (hasCompletedEss === 'Yes' && essShareMethod) {
      items.push({ linkId: 'ess-share-method', answer: [{ valueString: essShareMethod }] });
      if (essUrl && essFile) {
        items.push({
          linkId: 'ess-upload',
          answer: [{ valueAttachment: { url: essUrl, contentType: essFile.type, title: essFile.name } }],
        });
      }
    }

    onSubmit({ resourceType: 'QuestionnaireResponse', status: 'completed', item: items });
  };

  const isBusy = submitting || uploading;

  let continueLabel = 'Continue';
  if (uploading) {
    continueLabel = 'Uploading…';
  } else if (submitting) {
    continueLabel = 'Submitting…';
  }

  return (
    <Stack gap="lg">
      {error && (
        <Alert color="red" title="Error">
          {error}
        </Alert>
      )}

      <Radio.Group
        label={
          <Text fw={500}>
            Has the patient had a previous sleep study (HSAT or in-lab)? <span style={{ color: 'red' }}>*</span>
          </Text>
        }
        value={previousSleepStudy}
        onChange={setPreviousSleepStudy}
      >
        <Group mt="xs">
          <Radio value="Yes" label="Yes" />
          <Radio value="No" label="No" />
        </Group>
      </Radio.Group>

      <Radio.Group
        label={
          <Text fw={500}>
            Has the patient ever used a CPAP or BiPAP? <span style={{ color: 'red' }}>*</span>
          </Text>
        }
        value={cpapBipapUse}
        onChange={setCpapBipapUse}
      >
        <Group mt="xs">
          <Radio value="Yes" label="Yes" />
          <Radio value="No" label="No" />
          <Radio value="Could not tolerate" label="Could not tolerate" />
        </Group>
      </Radio.Group>

      <Radio.Group
        label={
          <Text fw={500}>
            Has the patient previously used an oral appliance? <span style={{ color: 'red' }}>*</span>
          </Text>
        }
        value={oralApplianceUse}
        onChange={setOralApplianceUse}
      >
        <Group mt="xs">
          <Radio value="Yes" label="Yes" />
          <Radio value="No" label="No" />
          <Radio value="Could not tolerate" label="Could not tolerate" />
        </Group>
      </Radio.Group>

      <Radio.Group
        label={
          <Text fw={500}>
            Has the patient had any kind of surgery for sleep apnea? <span style={{ color: 'red' }}>*</span>
          </Text>
        }
        value={sleepApneaSurgery}
        onChange={setSleepApneaSurgery}
      >
        <Group mt="xs">
          <Radio value="Yes" label="Yes" />
          <Radio value="No" label="No" />
        </Group>
      </Radio.Group>

      <Checkbox.Group
        label={<Text fw={500}>Does the patient have any of the following?</Text>}
        value={clinicalConditions}
        onChange={(next) => setClinicalConditions(applyNoneOfTheAboveExclusion(clinicalConditions, next))}
      >
        <Stack mt="xs" gap="xs">
          {CLINICAL_CONDITIONS.map((c) => (
            <Checkbox key={c.code} value={c.code} label={c.display} />
          ))}
        </Stack>
      </Checkbox.Group>

      <Radio.Group
        label={
          <Text fw={500}>
            Does the patient have a completed STOP-BANG questionnaire? <span style={{ color: 'red' }}>*</span>
          </Text>
        }
        value={hasCompletedStopbang}
        onChange={(v) => {
          setHasCompletedStopbang(v);
          setStopbangShareMethod('');
          setStopbangFile(null);
        }}
      >
        <Group mt="xs">
          <Radio value="Yes" label="Yes" />
          <Radio value="No" label="No" />
        </Group>
      </Radio.Group>

      {hasCompletedStopbang === 'Yes' && (
        <Radio.Group
          label={
            <Text fw={500}>
              How would you like to share the STOP-BANG results? <span style={{ color: 'red' }}>*</span>
            </Text>
          }
          value={stopbangShareMethod}
          onChange={(v) => {
            setStopbangShareMethod(v);
            setStopbangFile(null);
          }}
        >
          <Group mt="xs">
            <Radio value="Upload" label="Upload" />
            <Radio value="Fill in" label="Fill in" />
          </Group>
        </Radio.Group>
      )}

      {hasCompletedStopbang === 'Yes' && stopbangShareMethod === 'Upload' && (
        <FileDropZone file={stopbangFile} onChange={setStopbangFile} disabled={isBusy} />
      )}

      {hasCompletedStopbang === 'Yes' && stopbangShareMethod === 'Fill in' && (
        <Text size="sm" c="dimmed" mt="xs">
          You'll enter the STOP-BANG results in the next step.
        </Text>
      )}

      <Radio.Group
        label={
          <Text fw={500}>
            Does the patient have a completed Epworth Sleepiness Scale (ESS)? <span style={{ color: 'red' }}>*</span>
          </Text>
        }
        value={hasCompletedEss}
        onChange={(v) => {
          setHasCompletedEss(v);
          setEssShareMethod('');
          setEssFile(null);
        }}
      >
        <Group mt="xs">
          <Radio value="Yes" label="Yes" />
          <Radio value="No" label="No" />
        </Group>
      </Radio.Group>

      {hasCompletedEss === 'Yes' && (
        <Radio.Group
          label={
            <Text fw={500}>
              How would you like to share the ESS results? <span style={{ color: 'red' }}>*</span>
            </Text>
          }
          value={essShareMethod}
          onChange={(v) => {
            setEssShareMethod(v);
            setEssFile(null);
          }}
        >
          <Group mt="xs">
            <Radio value="Upload" label="Upload" />
            <Radio value="Fill in" label="Fill in" />
          </Group>
        </Radio.Group>
      )}

      {hasCompletedEss === 'Yes' && essShareMethod === 'Upload' && (
        <FileDropZone file={essFile} onChange={setEssFile} disabled={isBusy} />
      )}

      {hasCompletedEss === 'Yes' && essShareMethod === 'Fill in' && (
        <Text size="sm" c="dimmed" mt="xs">
          You'll enter the Epworth Sleepiness Scale results in the next step.
        </Text>
      )}

      <Group justify="flex-end" mt="md">
        {onBack && (
          <Button variant="subtle" color="blue" onClick={onBack} disabled={isBusy}>
            Back
          </Button>
        )}
        <Button
          onClick={() => {
            handleSubmit().catch(console.error);
          }}
          loading={isBusy}
          disabled={isBusy}
        >
          {continueLabel}
        </Button>
      </Group>
    </Stack>
  );
}
